// frontend/src/utils/useFormValidation.js
// 
// フォームバリデーション用のカスタムフック
// 

import { useState, useCallback, useMemo } from 'react';
import { validationRules } from './validationRules';

/**
 * フォームバリデーション用のカスタムフック
 * 
 * @param {string} entityType - 'user', 'delivery_address', 'insurance_card'
 * @param {object} options - オプション設定
 * @param {boolean} options.validateOnChange - 入力時にリアルタイムバリデーションを行うか（デフォルト: false）
 * @param {boolean} options.validateOnBlur - フォーカスアウト時にバリデーションを行うか（デフォルト: true）
 * @returns {object} - バリデーション関連の関数とstate
 */
export const useFormValidation = (entityType, options = {}) => {
  const {
    validateOnChange = false,
    validateOnBlur = true
  } = options;

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // エンティティタイプに対応するルールを取得
  const rules = useMemo(() => {
    return validationRules[entityType] || {};
  }, [entityType]);

  /**
   * 単一フィールドのバリデーション
   * @param {string} fieldName - フィールド名
   * @param {any} value - 検証する値
   * @param {object} formData - フォーム全体のデータ（相互依存バリデーション用）
   * @returns {string|null} - エラーメッセージ（エラーがない場合はnull）
   */
  const validateField = useCallback((fieldName, value, formData = {}) => {
    const rule = rules[fieldName];
    if (!rule) return null;

    // 必須チェック
    if (rule.required) {
      if (value === null || value === undefined) {
        return rule.errorMessages.required || `${rule.label}は必須です`;
      }
      
      if (typeof value === 'string' && value.trim().length === 0) {
        return rule.errorMessages.required || `${rule.label}は必須です`;
      }
      
      // ファイル型の場合
      if (rule.required && !(value instanceof File) && typeof value === 'object' && !value) {
        return rule.errorMessages.required || `${rule.label}は必須です`;
      }
    }

    // カスタムバリデーション
    if (rule.validate) {
      const errorKey = rule.validate(value, formData);
      if (errorKey) {
        return rule.errorMessages[errorKey] || `${rule.label}が正しくありません`;
      }
    }

    return null;
  }, [rules]);

  /**
   * フォーム全体のバリデーション
   * @param {object} formData - フォーム全体のデータ
   * @param {array} fieldsToValidate - 検証するフィールド名の配列（省略時は全フィールド）
   * @returns {boolean} - バリデーション成功時true
   */
  const validateForm = useCallback((formData, fieldsToValidate = null) => {
    const newErrors = {};
    let hasError = false;

    const fields = fieldsToValidate || Object.keys(rules);

    fields.forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName], formData);
      if (error) {
        newErrors[fieldName] = error;
        hasError = true;
      }
    });

    setErrors(newErrors);
    return !hasError;
  }, [rules, validateField]);

  /**
   * フィールドの値が変更された時の処理
   * @param {string} fieldName - フィールド名
   * @param {any} value - 新しい値
   * @param {object} formData - フォーム全体のデータ
   */
  const handleFieldChange = useCallback((fieldName, value, formData = {}) => {
    // リアルタイムバリデーションが有効、またはすでにエラーがある場合のみ検証
    if (validateOnChange || errors[fieldName]) {
      const error = validateField(fieldName, value, formData);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[fieldName] = error;
        } else {
          delete newErrors[fieldName];
        }
        return newErrors;
      });
    }
  }, [errors, validateField, validateOnChange]);

  /**
   * フィールドからフォーカスが外れた時の処理
   * @param {string} fieldName - フィールド名
   * @param {any} value - 現在の値
   * @param {object} formData - フォーム全体のデータ
   */
  const handleFieldBlur = useCallback((fieldName, value, formData = {}) => {
    // タッチ状態を記録
    setTouched(prev => ({ ...prev, [fieldName]: true }));

    // フォーカスアウト時のバリデーションが有効な場合のみ検証
    if (validateOnBlur) {
      const error = validateField(fieldName, value, formData);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[fieldName] = error;
        } else {
          delete newErrors[fieldName];
        }
        return newErrors;
      });
    }
  }, [validateField, validateOnBlur]);

  /**
   * エラーをクリア
   * @param {string|array} fields - クリアするフィールド名（省略時は全フィールド）
   */
  const clearErrors = useCallback((fields = null) => {
    if (!fields) {
      setErrors({});
    } else if (Array.isArray(fields)) {
      setErrors(prev => {
        const newErrors = { ...prev };
        fields.forEach(field => delete newErrors[field]);
        return newErrors;
      });
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fields];
        return newErrors;
      });
    }
  }, []);

  /**
   * タッチ状態をクリア
   */
  const clearTouched = useCallback(() => {
    setTouched({});
  }, []);

  /**
   * すべてをリセット
   */
  const reset = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  /**
   * エラーがあるかどうか
   */
  const hasErrors = useMemo(() => {
    return Object.keys(errors).length > 0;
  }, [errors]);

  /**
   * 特定のフィールドにエラーがあるかどうか
   * @param {string} fieldName - フィールド名
   * @returns {boolean}
   */
  const hasFieldError = useCallback((fieldName) => {
    return !!errors[fieldName];
  }, [errors]);

  /**
   * 特定のフィールドがタッチされたかどうか
   * @param {string} fieldName - フィールド名
   * @returns {boolean}
   */
  const isFieldTouched = useCallback((fieldName) => {
    return !!touched[fieldName];
  }, [touched]);

  /**
   * フィールドのエラーメッセージを取得
   * @param {string} fieldName - フィールド名
   * @returns {string|null}
   */
  const getFieldError = useCallback((fieldName) => {
    return errors[fieldName] || null;
  }, [errors]);

  /**
   * フィールドが必須かどうか
   * @param {string} fieldName - フィールド名
   * @returns {boolean}
   */
  const isRequired = useCallback((fieldName) => {
    const rule = rules[fieldName];
    return rule ? rule.required : false;
  }, [rules]);

  /**
   * フィールドのラベルを取得
   * @param {string} fieldName - フィールド名
   * @returns {string}
   */
  const getFieldLabel = useCallback((fieldName) => {
    const rule = rules[fieldName];
    return rule ? rule.label : fieldName;
  }, [rules]);

  return {
    // State
    errors,
    touched,
    hasErrors,

    // バリデーション関数
    validateField,
    validateForm,

    // イベントハンドラー
    handleFieldChange,
    handleFieldBlur,

    // ユーティリティ
    clearErrors,
    clearTouched,
    reset,
    hasFieldError,
    isFieldTouched,
    getFieldError,
    isRequired,
    getFieldLabel
  };
};

export default useFormValidation;
